/**
 * 줄서기(Fila Virtual) 고객 API — 무인증.
 *
 * @see #781 (menu PR3) / admin#2111
 * @see olulo-mx-admin/docs/plans/2026-07-16-waitlist-design.md §4
 * @see olulo-mx-admin app/Http/Controllers/Api/V1/WaitlistController.php (실측 계약)
 *
 * ## 인증 모델
 * 등록 응답의 `access_token`(uuid) 이 곧 자격증명이다 — 이후 상태 조회/도착/이탈은
 * 토큰만으로 접근한다. 따라서 토큰은 URL 에 싣지 않고 localStorage 에만 보관한다
 * (@see components/waitlist/storage.ts).
 *
 * ## 경로 주의 (설계 문서 §4 표기와 실제 라우트 차이)
 * 설계 문서는 `/api/stores/{store}/waitlist` 로 적고 있으나, 실제 등록 라우트
 * (routes/customer-api.php)는 **`/api/v1/` 접두사 + 숫자 storeId**(`whereNumber`) 다.
 * 슬러그는 바인딩되지 않으므로 `/fila/{storeSlug}` 진입 시 storeApi.getStore 로
 * 숫자 id 를 먼저 해석해야 한다.
 */
import { ApiClientError, apiClient, validatePathParam } from './client';
import { recoverSafeRead } from './recoverSafeRead';
import { READ_REQUEST_ID_HEADER } from '@/utils/transportDiagnostics';
import type { Locale } from '@/config/locales';

/** 대기 팀 상태 — admin `App\Enums\WaitlistStatus` 와 동기화. */
export type WaitlistStatus = 'waiting' | 'called' | 'seated' | 'cancelled' | 'preparing' | 'ready';

/**
 * 줄서기 큐 종류 — admin Task 4/8 "테이블+픽업" 확장(#794).
 *
 * `table` 은 기존 좌석 대기, `pickup` 은 카운터 주문 대기다. 등록 요청과 화면 상태
 * 복원(storage.ts) 양쪽에서 사용한다.
 */
export type WaitlistQueueType = 'table' | 'pickup';

/** 신규 접수 상태 — admin `WaitlistIntakeStatus` 고객 계약과 동기화(#2181). */
export type WaitlistQueueIntakeStatus = 'open' | 'paused' | 'unavailable';

/** 고객에게 공개 가능한 일시정지 사유. 운영자 메모는 이 계약에 포함하지 않는다. */
export type WaitlistPauseReason =
  | 'capacity'
  | 'out_of_stock'
  | 'closing'
  | 'manual'
  | 'other';

const WAITLIST_PAUSE_REASON_VALUES = new Set<WaitlistPauseReason>([
  'capacity',
  'out_of_stock',
  'closing',
  'manual',
  'other',
]);

/** API 런타임 값이 고객 공개 가능 enum 인지 확인한다. */
export function isWaitlistPauseReason(value: unknown): value is WaitlistPauseReason {
  return (
    typeof value === 'string' &&
    WAITLIST_PAUSE_REASON_VALUES.has(value as WaitlistPauseReason)
  );
}

export interface WaitlistQueueStatus {
  /**
   * 현재 신규 접수 가능 여부. paused 와 unavailable 모두 false 이므로
   * `intake_status` 와 함께 해석해야 한다.
   */
  enabled: boolean;
  waiting_count: number;
  est_minutes: number;
  /**
   * 신규 계약 필드. optional 은 admin 선배포 전 구서버 응답과의 호환을 위한 것이다.
   * 부재 시 enabled=true → open, false → unavailable 로 해석한다.
   */
  intake_status?: WaitlistQueueIntakeStatus;
  pause_reason?: WaitlistPauseReason | null;
  /** 향후 예약 재개 계약을 위한 additive 필드. 현재 P0 서버는 내려주지 않는다. */
  resume_at?: string | null;
}

export interface WaitlistTableQueueStatus extends WaitlistQueueStatus {
  max_party_size: number;
}

export interface WaitlistMarketingConsentAvailable {
  available: true;
  status: 'available';
  default_checked: false;
  version: string;
  hash: string;
  locale: string;
  /**
   * 접힌 상태에서 보여줄 요약 라벨 평문(공지 v2, admin#2491). 예: es-MX
   * "Recibir cupones y novedades (opcional)".
   *
   * v1 서버는 이 필드를 주지 않는다 — 없으면 {@link text} 전문을 그대로 라벨로
   * 렌더하는 기존 동작으로 폴백한다(#882 하위 호환).
   */
  summary?: string;
  /** 요약 라벨 평문의 sha256. 전송 계약에는 쓰지 않고 진단·추적용이다. */
  summary_hash?: string;
  /** 동의 전문 평문. 단락 구분은 `\n\n`, 줄바꿈은 `\n`, URL 은 평문이다. */
  text: string;
  text_hash: string;
}

export interface WaitlistMarketingConsentUnavailable {
  available: false;
  status: 'unavailable';
  default_checked: false;
  version: null;
  hash: null;
  locale: null;
  /** 공지 v2 서버는 unavailable 에도 키를 내려보낼 수 있다 — 항상 null 이다. */
  summary?: null;
  summary_hash?: null;
  text: null;
  text_hash: null;
}

/** Platform Care 활성 Store의 선택형 마케팅 동의 문구 계약. */
export type WaitlistMarketingConsent =
  | WaitlistMarketingConsentAvailable
  | WaitlistMarketingConsentUnavailable;

/** 구서버 응답까지 포함해 큐의 신규 접수 상태를 정규화한다. */
export function getWaitlistQueueIntakeStatus(
  queue: Pick<WaitlistQueueStatus, 'enabled' | 'intake_status'>,
): WaitlistQueueIntakeStatus {
  return queue.intake_status ?? (queue.enabled ? 'open' : 'unavailable');
}

/** 설정상 존재하며 고객에게 보여줄 수 있는 큐인가. paused 는 존재하는 큐다. */
export function isWaitlistQueueAvailable(
  queue: Pick<WaitlistQueueStatus, 'enabled' | 'intake_status'>,
): boolean {
  return getWaitlistQueueIntakeStatus(queue) !== 'unavailable';
}

/** 지금 신규 대기번호를 발행할 수 있는 큐인가. */
export function isWaitlistQueueOpen(
  queue: Pick<WaitlistQueueStatus, 'enabled' | 'intake_status'>,
): boolean {
  return getWaitlistQueueIntakeStatus(queue) === 'open';
}

/**
 * 줄서기 error_code — admin `App\Exceptions\WaitlistException` named 생성자와 1:1.
 *
 * `TOO_FAR` 와 `GPS_UNAVAILABLE` 은 반드시 구분해 안내한다 (설계 §8-3):
 * 전자는 "가까이 오세요", 후자는 "호스트에게 문의" — 위치 거부 사용자를 줄에서
 * 밀어내지 않기 위함이다.
 */
export type WaitlistErrorCode =
  | 'WAITLIST_CLOSED'
  | 'DUPLICATE_PARTY'
  | 'PARTY_SIZE_TOO_LARGE'
  | 'TOO_FAR'
  | 'GPS_UNAVAILABLE'
  | 'INVALID_TRANSITION'
  | 'QR_INACTIVE'
  | 'STORE_NOT_FOUND'
  /** 요청한 큐(테이블/픽업)가 비활성 — #794 2종 확장. */
  | 'QUEUE_INACTIVE'
  /** 요청한 큐는 운영 중이지만 신규 접수가 일시정지됨 — admin#2181. */
  | 'QUEUE_PAUSED'
  /**
   * 픽업 줄서기 게이트 매장(#804) — `waitlist_token` 부재/불일치/타매장/픽업 큐 아님.
   * @see docs/plans/2026-07-24-pickup-discovery-waitlist-gate-design.md §3.1
   */
  | 'WAITLIST_REQUIRED'
  /** 게이트 매장에서 파티 상태가 called 가 아님(waiting/preparing/ready/seated/cancelled). */
  | 'WAITLIST_NOT_CALLED';

/** GET waitlist-status 응답 — FormScreen 진입 게이트 + 미리보기 수치. */
export interface WaitlistStatusData {
  /** 접수 중 여부. false 면 신규 등록 차단(기존 대기 팀은 유지 — 설계 §8-1). */
  enabled: boolean;
  max_party_size: number;
  gps_verification: boolean;
  whatsapp_notify: boolean;
  /** 현재 대기 팀 수 (신규 등록자가 서게 될 위치). */
  waiting_count: number;
  est_minutes: number;
  /** `?p=` 유효 QR 로 진입한 경우에만 존재. */
  qr: { slug: string; name: string } | null;
  /**
   * 테이블/픽업 큐별 세부 현황 — admin Task 4 확장(#794). **구서버 호환을 위해
   * optional**: 값이 없으면 FE 는 기존 단일 큐(top-level 필드) 동작으로 폴백한다.
   */
  queues?: {
    table: WaitlistTableQueueStatus;
    pickup: WaitlistQueueStatus;
  };
  /**
   * 매장 메뉴/위치 메타 — 선택/완료 화면의 "메뉴 보기"·"위치 보기" 버튼용(#794).
   * optional: 구서버 호환.
   */
  store_meta?: {
    menu_view_url: string | null;
    menu_view_kind: 'pdf' | 'digital' | null;
    menu_urls?: { digital: string | null; pdf: string | null };
    logo_url: string | null;
    maps_url: string | null;
    /**
     * 픽업 줄서기 게이트 매장(#804) — true 면 called 화면에 "앱에서 주문하기" 버튼을
     * 노출한다. optional: 구서버 호환(부재 시 버튼 미노출).
     */
    pickup_requires_waitlist?: boolean;
    /** "앱에서 주문하기" 버튼의 `/restaurant/{slug}` 이동 대상. optional: 구서버 호환. */
    store_slug?: string;
  };
  /**
   * Platform Care 런타임 gate와 exact Store revision이 모두 활성일 때만
   * `available=true`와 서버 소유 locale 문구/증거가 내려온다.
   */
  marketing_consent?: WaitlistMarketingConsent;
}

export interface WaitlistStatusResponse {
  data: WaitlistStatusData;
}

export interface JoinWaitlistRequest {
  party_size: number;
  /** 구분자 없는 숫자열 (서버가 dial_code 와 합성해 E.164 생성). */
  phone: string;
  /** `+52` 형식. */
  dial_code: string;
  locale: string;
  sms_fallback?: boolean;
  /**
   * 등록할 큐 종류(#794). 생략 시 서버 기본값은 `table`. `pickup` 이면 서버가
   * `party_size` 를 무시하고 1로 고정하므로 FE 는 픽업 등록 폼에서 인원 선택
   * UI 자체를 노출하지 않는다.
   */
  queue_type?: WaitlistQueueType;
  /** 선택한 경우에만 전송한다. 미선택/OFF에서는 notice 증거도 함께 생략한다. */
  marketing_consent?: true;
  marketing_notice_version?: string;
  marketing_notice_hash?: string;
  marketing_notice_locale?: string;
}

export interface JoinWaitlistData {
  ticket_no: string;
  /** 이 응답이 access_token 을 노출하는 유일한 지점 — 반드시 보관할 것. */
  access_token: string;
  position: number;
  est_minutes: number;
  party_size: number;
  channel: string;
  joined_at: string;
  /**
   * 등록된 큐 종류(#794) — 요청 시 `queue_type` 을 생략해도 서버가 결정한 값을
   * 그대로 되돌려준다. 구서버 응답에는 없을 수 있어 optional.
   */
  queue_type?: WaitlistQueueType;
  /**
   * 마케팅 동의 처리 결과 — 서버는 이미 내려주고 있으나 FE 타입에 없던 필드다
   * (admin#2472 셀프 철회 UI 를 붙이며 계약을 명시).
   *
   * - `recorded`: 동의 증거가 실제로 저장됐다 → 철회 링크를 노출한다.
   * - `not_recorded`: 체크했으나 서버가 기록하지 않았다(문구 revision 불일치 등).
   * - `not_requested`: 애초에 동의를 보내지 않았다.
   *
   * optional 은 구서버 호환 — 부재 시 "기록 없음"으로 보수적으로 취급한다.
   */
  marketing_consent?: { status: JoinWaitlistMarketingConsentStatus };
}

/** 등록 응답의 마케팅 동의 처리 결과 — admin 계약(#2472). */
export type JoinWaitlistMarketingConsentStatus = 'recorded' | 'not_recorded' | 'not_requested';

export interface JoinWaitlistResponse {
  success: boolean;
  data: JoinWaitlistData;
}

/** GET /api/v1/waitlist/{access_token} — 15s 폴링 대상. */
export interface WaitlistPartyData {
  ticket_no: string;
  status: WaitlistStatus;
  status_label: string;
  party_size: number;
  /** 앞 팀 수. waiting 이 아니면 항상 0. */
  position: number;
  est_minutes: number;
  channel: string;
  /**
   * 알림을 보낼 연락처의 **서버 마스킹 표시값**(admin#2504) — 예 `+52 55 **** 1111`.
   *
   * ## 왜 원문(phone_e164) 대신 이 값을 표시하나
   * 번호 금고(vault) 전환 뒤 서버는 원문을 애플리케이션 응답에 싣지 않는다. 화면이
   * 필요로 한 것은 "내가 준 그 번호가 맞나"를 알아보는 것뿐이라, 마스킹 값이면 충분하고
   * 원문은 불필요한 노출면이다. 그래서 표시 계층은 이 필드를 1순위로 쓴다.
   *
   * optional·nullable 인 이유는 두 축이다 — 이 필드를 모르는 구서버(undefined)와,
   * 알지만 표시할 연락처가 없는 응답(null). 둘 다 "이 값으로는 표시할 수 없다"로
   * 동일하게 취급하고 다음 순위로 내려간다.
   */
  contact_display?: string | null;
  /**
   * E.164 원문. **vault 쓰기 모드에서는 null 이 될 수 있다**(admin#2504).
   *
   * 필수 string 으로 두면 `null` 이 타입상 존재하지 않는 값이 되어 화면이
   * `undefined` 없는 안전한 폴백을 짤 수 없다 — 계약이 부재를 표현해야 표시 계층이
   * `contact_display ?? phone_e164 ?? …` 순서를 성립시킬 수 있다.
   */
  phone_e164: string | null;
  table_label: string | null;
  cancel_reason: string | null;
  gps_verified_at: string | null;
  joined_at: string;
  called_at: string | null;
  /** LLAMAR 시 서버가 영속화한 절대 만료 시각. 구서버 호환을 위해 optional. */
  call_expires_at?: string | null;
  /** 이 상태 응답을 만든 서버 UTC 시각 — 고객 기기 시계 오차 보정용. */
  server_time_utc?: string;
  seated_at: string | null;
  /** 큐 종류(#794). 구서버 응답에는 없을 수 있어 optional. */
  queue_type?: WaitlistQueueType;
  /** 픽업 큐 전용 — 카운터에서 주문을 접수한 주문번호. */
  order_no?: string | null;
  order_taken_at?: string | null;
  /** 픽업 준비 완료 시각(주방/카운터가 기록). */
  ready_at?: string | null;
  /** 매장 도착(입장 GPS 인증 등) 시각 — `arrive` 응답과 동일 값. */
  arrived_at?: string | null;
  /** 픽업 큐 전용 — 매장이 설정한 예상 준비 소요 분. */
  pickup_prep_minutes?: number;
  /**
   * 이 연락처의 현재 마케팅 수신 상태 — 철회 링크 노출의 서버 권위(admin#2493).
   *
   * ## 왜 상태 응답에 실리나
   * 동의는 등록 1건이 아니라 **연락처 단위**로 산다(admin#2491 동의 v2). 그래서
   * "지난 방문에 동의하고 이번엔 체크하지 않은 재방문 고객"이나 "단말/브라우저를
   * 바꿔 로컬 플래그를 잃은 고객"은 여전히 수신 대상인데도 화면에서 철회에 도달할
   * 수단이 없었다(#884). 서버가 불리언 하나로 현재 상태를 알려주면 로컬 플래그
   * 없이도 링크를 정확히 그릴 수 있다.
   *
   * 번호·금고 id 같은 식별자는 담기지 않으며 access_token 보유자에게만 나가므로
   * 새로운 개인정보 노출면이 아니다.
   *
   * optional 은 구서버 호환 — 부재(undefined) 시 기존 localStorage 플래그 폴백으로
   * 판정한다(있다고 단정하지도, 없다고 단정하지도 않는다).
   */
  marketing_consent?: WaitlistPartyMarketingConsentState;
}

/**
 * 상태 응답의 연락처 단위 마케팅 수신 상태 (admin#2493 / admin#2496).
 *
 * 두 축이 한 객체에 들어 있다. **철회 링크 노출을 결정하는 것은 `current` 하나뿐**
 * 이고(연락처 전역 통제권), **팔로우 토글을 결정하는 것은 `store_followed`**다(이
 * 파티의 매장 한 곳). 그래서 소비자(useMarketingConsentWithdrawal /
 * useStoreMarketingFollow)는 불리언을 풀어 받지 않고 이 객체를 통째로 받는다
 * (인접한 optional boolean 인자끼리 순서가 뒤바뀌는 사고 방지).
 *
 * 발송 가능 = `current && store_followed` (follow 설계 §3.1).
 */
export interface WaitlistPartyMarketingConsentState {
  /** true 면 이 연락처가 지금 마케팅 수신 대상 — 철회 링크를 노출한다. */
  current: boolean;
  /**
   * 이 파티의 **매장**에 활성 매장 억제가 없는가 — 팔로우 토글의 서버 권위(#885).
   *
   * ## 왜 optional 인가 (계약상 필수인데도)
   * admin#2496 이 붙은 서버는 `current` 와 함께 항상 내려준다. 그러나 `current` 만
   * 아는 서버(admin#2493 선배포분)가 실재하고, 그 응답에서 이 값은 `undefined` 다.
   * 필수 boolean 으로 두면 `undefined === true` 가 false 로 접혀 **팔로우 중인
   * 고객에게 "이 매장 프로모션을 받지 않습니다"라는 거짓 안내**를 띄운다. 타입이
   * 부재를 표현해야 화면이 "모르면 토글을 감춘다"를 선택할 수 있다.
   */
  store_followed?: boolean;
}

export interface WaitlistPartyResponse {
  data: WaitlistPartyData;
}

export interface ArriveWaitlistRequest {
  lat?: number;
  lng?: number;
}

export interface ArriveWaitlistResponse {
  success: boolean;
  data: {
    ticket_no: string;
    status: WaitlistStatus;
    gps_verified_at: string | null;
    /** admin Task 4 확장(#794) — 구서버 호환을 위해 optional. */
    arrived_at?: string | null;
  };
}

export interface LeaveWaitlistResponse {
  success: boolean;
  data: {
    ticket_no: string;
    status: WaitlistStatus;
    cancel_reason: string | null;
  };
}

/**
 * DELETE /api/v1/waitlist/{access_token}/marketing-consent 응답 (admin#2472).
 *
 * ## 왜 전 필드가 optional 인가
 * 이 엔드포인트는 FE 와 동시 릴리즈되는 신규 계약이라 응답 형태가 확정 전이다.
 * 호출부는 `status === 'withdrawn'` 만 신뢰하고, 나머지는 없어도 성공으로 취급한다
 * (2xx 자체가 "서버에 동의 증거가 더는 없다"는 계약이므로 파싱은 관대하게 둔다).
 */
export interface WithdrawMarketingConsentData {
  status?: 'withdrawn' | string;
  withdrawn_at?: string | null;
}

export interface WithdrawMarketingConsentResponse {
  data?: WithdrawMarketingConsentData;
}

/** 철회할 동의 증거가 서버에 없을 때의 error_code — 기존 waitlist 에러 형식과 동일. */
export const WAITLIST_CONSENT_NOT_FOUND_CODE = 'CONSENT_NOT_FOUND';

/**
 * "철회할 동의가 애초에 없음" 응답인가 — 사용자 관점에서는 성공과 구분되지 않는다.
 *
 * ## 왜 error_code 만 보는가 (맨 404 는 완료가 아니다)
 * 이 판정은 화면에 "완료했습니다. 이제 이 번호로는 프로모션을 받지 않습니다"를
 * 띄운다 — 틀리면 개인정보 통제권에 대한 거짓 안내가 된다. 그런데 404 는 이
 * 엔드포인트에서 두 가지 서로 다른 사건을 낸다.
 *
 * 1. 컨트롤러가 "이 연락처에 닫을 동의 증거가 없다"고 판단한 404 — `error_code:
 *    'CONSENT_NOT_FOUND'` 가 실린다(admin WaitlistController). 완료로 봐도 좋다.
 * 2. 라우트 모델 바인딩(`{party:access_token}`)이 토큰을 찾지 못해 낸 404 — 본문에
 *    `error_code` 가 없다. 서버는 **아무 것도 철회하지 않았다.** 착석 화면은 폴링을
 *    멈추므로(useWaitlistParty 의 terminal 처리) 토큰이 죽은 줄 모르는 사용자가 이
 *    404 를 만날 수 있고, 이를 완료로 삼키면 수신은 계속되는데 끊겼다고 안내한다.
 *
 * admin 은 자기 핸들러의 404 에 항상 error_code 를 싣는다 — 관대 파싱(맨 404 수용)의
 * 근거는 사라졌고, 남은 것은 2번을 1번으로 오독할 위험뿐이다. 그래서 **error_code 만**
 * 신뢰한다. status 는 보지 않으므로 서버가 같은 코드를 410 등으로 옮겨도 따라간다.
 */
export function isWaitlistConsentAbsentError(error: unknown): boolean {
  if (!(error instanceof ApiClientError)) {
    return false;
  }
  // 409 `CONSENT_WITHDRAWAL_UNAVAILABLE` 과는 code 값으로 자연히 배타적이다 —
  // 아래 술어도 code 를 우선 신뢰하므로 두 판정이 동시에 참이 될 수 없다.
  return error.code === WAITLIST_CONSENT_NOT_FOUND_CODE;
}

/**
 * 철회를 처리할 수 없었을 때의 error_code — **완료가 아니다**(admin#2493).
 *
 * 승계 수신자(이번 방문에 체크하지 않은 고객)의 철회 요청에서 연락처 신원이
 * 모호하거나(금고 키 회전) 내부 상태가 어긋나면 서버는 철회를 수행하지 않고 운영
 * 이관 신호만 남긴다. 사용자에게는 "완료"도 "일반 오류"도 아닌 전용 안내가 필요하다.
 */
export const WAITLIST_CONSENT_WITHDRAWAL_UNAVAILABLE_CODE = 'CONSENT_WITHDRAWAL_UNAVAILABLE';

/**
 * "지금은 철회를 처리할 수 없음" 응답인가 (409 `CONSENT_WITHDRAWAL_UNAVAILABLE`).
 *
 * ## code 가 있으면 code 가 이긴다
 * 서버가 error_code 를 실어 보냈다면 그것이 서버의 의도 표명이다. status 를 OR 로
 * 두면 맨 409 판정이 상반된 code 를 덮어쓴다 — 예컨대 서버가 "이미 철회됨"을 REST
 * 관례대로 409 + `CONSENT_NOT_FOUND` 로 옮기는 순간, 실제로는 철회된 고객에게
 * "처리하지 못했습니다, 메일을 보내세요"를 띄우게 된다. 그래서 code 가 있으면 code
 * 만 보고, **code 가 없을 때만** status 409 로 폴백한다(동시 개발 중인 계약이라
 * error_code 표기가 아직 빠질 수 있어 폴백 자체는 남긴다).
 *
 * 이 판정이 {@link isWaitlistConsentAbsentError} 보다 먼저 호출되므로(훅) 두 술어가
 * 동시에 참이 되면 완료/미완료가 갈린다 — "둘 다 미완료라 안전"하지 않다.
 */
export function isWaitlistConsentWithdrawalUnavailableError(error: unknown): boolean {
  if (!(error instanceof ApiClientError)) {
    return false;
  }
  if (error.code) {
    return error.code === WAITLIST_CONSENT_WITHDRAWAL_UNAVAILABLE_CODE;
  }
  return error.status === 409;
}

/**
 * POST/DELETE /api/v1/waitlist/{access_token}/store-follow 응답 (admin#2496).
 *
 * PII 를 담지 않는다 — 번호·금고 id·증거 id 는 어느 필드에도 없다. `changed_at` 은
 * 이번 호출이 실제로 바꾼 시각이고, 아무 것도 바꾸지 않은 멱등 재호출은 마지막 토글
 * 시각(없으면 null)을 돌려준다.
 */
export interface WaitlistStoreFollowData {
  /** 호출 뒤의 매장 팔로우 상태 — 화면은 낙관적 값 대신 이 값으로 확정한다. */
  store_followed: boolean;
  /** 연락처 전역 수신 여부. false 면 팔로우해도 지금은 광고가 나가지 않는다. */
  current: boolean;
  changed_at?: string | null;
}

export interface WaitlistStoreFollowResponse {
  data: WaitlistStoreFollowData;
}

/**
 * v1 시절 매장 프라이버시 철회가 활성이라 토글로는 다시 켤 수 없음 — 409(admin#2496).
 *
 * 억제 강도 사다리(follow 설계 §1.4)에서 `customer_unsubscribe:customer_settings`
 * 는 `store_unfollow:waitlist_screen` 보다 강하다. 약한 사유는 강한 활성 행을 닫지
 * 못하므로 서버는 **아무 것도 바꾸지 않고** 이 코드를 돌려준다 — 해제 경로는 그
 * 매장의 새 동의(등록 화면 체크)뿐이라 전용 안내가 필요하다.
 */
export const WAITLIST_STORE_FOLLOW_RECONSENT_REQUIRED_CODE = 'STORE_FOLLOW_RECONSENT_REQUIRED';

/** 신원 모호·잠금 경합 등으로 지금은 처리 불가 — 409, 재시도로 풀릴 수 있다. */
export const WAITLIST_STORE_FOLLOW_UNAVAILABLE_CODE = 'STORE_FOLLOW_UNAVAILABLE';

/** 이 연락처에 동의 이력(금고)이 없어 켜고 끌 대상 자체가 없음 — 404. */
export const WAITLIST_STORE_FOLLOW_NOT_APPLICABLE_CODE = 'STORE_FOLLOW_NOT_APPLICABLE';

/**
 * "토글로는 다시 켤 수 없음"(409 `STORE_FOLLOW_RECONSENT_REQUIRED`) 응답인가.
 *
 * status 폴백을 두지 않는다 — 409 는 두 종류이고, 맨 409 를 이쪽으로 접으면 재시도로
 * 풀릴 일시적 실패를 "등록 때 다시 동의하세요"(막다른 안내)로 오독하게 된다. 맨 409
 * 는 {@link isWaitlistStoreFollowUnavailableError} 가 가져간다.
 */
export function isWaitlistStoreFollowReconsentRequiredError(error: unknown): boolean {
  return (
    error instanceof ApiClientError &&
    error.code === WAITLIST_STORE_FOLLOW_RECONSENT_REQUIRED_CODE
  );
}

/**
 * "지금은 처리할 수 없음"(409 `STORE_FOLLOW_UNAVAILABLE`) 응답인가.
 *
 * 철회 쪽 술어와 같은 규칙이다 — code 가 있으면 code 가 이기고, **code 가 없을 때만**
 * status 409 로 폴백한다(동시 개발 중인 계약이라 표기가 빠질 수 있다). 화면 처리는
 * 일반 오류와 같지만(재시도), 두 술어를 배타로 둬야 재동의 안내가 새지 않는다.
 */
export function isWaitlistStoreFollowUnavailableError(error: unknown): boolean {
  if (!(error instanceof ApiClientError)) {
    return false;
  }
  if (error.code) {
    return error.code === WAITLIST_STORE_FOLLOW_UNAVAILABLE_CODE;
  }
  return error.status === 409;
}

/**
 * "켜고 끌 대상이 없음"(404 `STORE_FOLLOW_NOT_APPLICABLE`) 응답인가 — 토글을 감춘다.
 *
 * code 가 없는 맨 404 도 같은 처리로 접는다. 그 404 의 정체는 두 가지뿐인데
 * (엔드포인트가 아직 없는 구서버 / 죽은 토큰) 어느 쪽이든 **이 화면에서 팔로우를
 * 조작할 수 없다**는 사실은 같다. 철회(#884)에서 맨 404 를 완료로 접지 않았던 것과
 * 모순이 아니다 — 그쪽은 "끝났다"는 거짓 안내가 되지만, 이쪽은 동작하지 않는 컨트롤을
 * 치우는 것이라 고객에게 거짓을 말하지 않는다.
 */
export function isWaitlistStoreFollowNotApplicableError(error: unknown): boolean {
  if (!(error instanceof ApiClientError)) {
    return false;
  }
  if (error.code) {
    return error.code === WAITLIST_STORE_FOLLOW_NOT_APPLICABLE_CODE;
  }
  return error.status === 404;
}

/**
 * menu 15-locale 코드 → admin `config/app.available_locales` 키.
 *
 * 스페인어만 어긋난다 — menu 는 `es`, admin 은 `es-MX` 를 키로 쓴다. 그대로 보내면
 * JoinWaitlistRequest 의 `in:` 검증에서 422 가 난다.
 */
const API_LOCALE_OVERRIDES: Partial<Record<Locale, string>> = {
  es: 'es-MX',
};

/** 등록 body 의 `locale` 값 — 알림(WhatsApp/SMS) 문구 언어로 저장된다. */
export function toApiLocale(locale: Locale): string {
  return API_LOCALE_OVERRIDES[locale] ?? locale;
}

export const waitlistApi = {
  /**
   * 매장 접수 현황 + QR 유효성.
   * GET /api/v1/stores/{storeId}/waitlist-status?p={qrSlug}
   *
   * 유효 QR 이면 서버가 스캔 카운트를 증가시키므로 진입당 1회만 호출한다.
   * 비활성/미존재 slug → 422 `QR_INACTIVE`.
   */
  getStatus: (
    storeId: number,
    locale: string,
    qrSlug?: string,
    signal?: AbortSignal,
  ) =>
    apiClient.get<WaitlistStatusResponse>(`/api/v1/stores/${storeId}/waitlist-status`, {
      params: {
        ...(qrSlug ? { p: qrSlug } : {}),
        locale,
      },
      signal,
    }),

  /**
   * 대기 등록.
   * POST /api/v1/stores/{storeId}/waitlist?p={qrSlug} → 201
   *
   * 422 `WAITLIST_CLOSED` / 409 `DUPLICATE_PARTY` / 422 `PARTY_SIZE_TOO_LARGE` /
   * 422 `QUEUE_INACTIVE`(요청한 `queue_type` 큐가 비활성 — #794) /
   * 422 `QUEUE_PAUSED`(화면 표시 후 해당 큐 신규 접수가 일시정지 — #809).
   * `p` 는 등록 소스 어트리뷰션(source_qr_id) 용 — body 가 아닌 쿼리로 전달한다
   * (JoinWaitlistRequest 규칙에 없고 컨트롤러가 `$request->string('p')` 로 읽는다).
   */
  join: (storeId: number, data: JoinWaitlistRequest, qrSlug?: string) =>
    apiClient.post<JoinWaitlistResponse>(`/api/v1/stores/${storeId}/waitlist`, data, {
      params: qrSlug ? { p: qrSlug } : undefined,
    }),

  /**
   * 상태 조회 (폴링 15s).
   * GET /api/v1/waitlist/{accessToken}
   */
  getParty: (accessToken: string, signal?: AbortSignal) => {
    const safeToken = validatePathParam(accessToken, 'accessToken');
    return recoverSafeRead('party-read', signal, (timeout, requestId) =>
      apiClient.get<WaitlistPartyResponse>(`/api/v1/waitlist/${safeToken}`, {
        signal,
        timeout,
        cache: 'no-store',
        diagnosticOperation: 'party-read',
        headers: requestId ? { [READ_REQUEST_ID_HEADER]: requestId } : undefined,
      }),
    );
  },

  /**
   * 도착 확인 (called → seated 대기).
   * POST /api/v1/waitlist/{accessToken}/arrive
   *
   * gps_verification 매장에서 좌표 미제공 시 422 `GPS_UNAVAILABLE`,
   * 반경 밖이면 422 `TOO_FAR`.
   */
  arrive: (accessToken: string, coords: ArriveWaitlistRequest = {}) => {
    const safeToken = validatePathParam(accessToken, 'accessToken');
    return apiClient.post<ArriveWaitlistResponse>(`/api/v1/waitlist/${safeToken}/arrive`, coords);
  },

  /**
   * 고객 이탈.
   * DELETE /api/v1/waitlist/{accessToken}
   */
  leave: (accessToken: string) => {
    const safeToken = validatePathParam(accessToken, 'accessToken');
    return apiClient.delete<LeaveWaitlistResponse>(`/api/v1/waitlist/${safeToken}`);
  },

  /**
   * 마케팅 동의 셀프 철회 (admin#2472).
   * DELETE /api/v1/waitlist/{accessToken}/marketing-consent
   *
   * 무인증 — 등록 응답의 access_token 이 곧 자격증명이다(다른 waitlist 엔드포인트와
   * 동일). 서버는 throttle 로 보호한다.
   *
   * 이미 철회된 경우에도 200 으로 동일 payload 를 돌려주며, 동의 증거 자체가 없으면
   * 404 `CONSENT_NOT_FOUND` 다 — 둘 다 사용자에게는 "철회 완료"이므로 호출부는
   * {@link isWaitlistConsentAbsentError} 로 404 를 성공과 같이 처리한다.
   *
   * 반면 409 `CONSENT_WITHDRAWAL_UNAVAILABLE`(admin#2493)은 **철회가 일어나지 않은**
   * 상태다 — 완료로 처리하지 말고 {@link isWaitlistConsentWithdrawalUnavailableError}
   * 로 갈라 전용 안내(다른 연락 수단)를 띄운다.
   */
  withdrawMarketingConsent: (accessToken: string, signal?: AbortSignal) => {
    const safeToken = validatePathParam(accessToken, 'accessToken');
    return apiClient.delete<WithdrawMarketingConsentResponse>(
      `/api/v1/waitlist/${safeToken}/marketing-consent`,
      { signal },
    );
  },

  /**
   * 매장 팔로우 — 이 파티의 매장 광고를 다시 받는다 (admin#2496).
   * POST /api/v1/waitlist/{accessToken}/store-follow
   *
   * 철회와 달리 **가역**이고 범위가 매장 하나다. 전역 동의(연락처 층)는 건드리지
   * 않으므로 새 동의 증거도 만들지 않는다 — 이미 살아 있는 동의를 어느 매장에 쓸지만
   * 고친다(follow 설계 §1.3).
   *
   * 활성 억제가 없으면 200 무동작(멱등). v1 프라이버시 철회가 활성이면 409
   * `STORE_FOLLOW_RECONSENT_REQUIRED` 로 **아무 것도 바뀌지 않는다**.
   */
  followStore: (accessToken: string, signal?: AbortSignal) => {
    const safeToken = validatePathParam(accessToken, 'accessToken');
    return apiClient.post<WaitlistStoreFollowResponse>(
      `/api/v1/waitlist/${safeToken}/store-follow`,
      undefined,
      { signal },
    );
  },

  /**
   * 매장 언팔로우 — 이 매장 광고만 끊는다 (admin#2496).
   * DELETE /api/v1/waitlist/{accessToken}/store-follow
   *
   * 순번·호출·픽업 안내(비광고)는 억제 테이블을 읽지 않으므로 그대로 도착한다.
   * 이미 언팔로우 상태면 200 무동작(멱등) — 옛 탭의 재전송이 새 억제 행을 만들지 않는다.
   */
  unfollowStore: (accessToken: string, signal?: AbortSignal) => {
    const safeToken = validatePathParam(accessToken, 'accessToken');
    return apiClient.delete<WaitlistStoreFollowResponse>(
      `/api/v1/waitlist/${safeToken}/store-follow`,
      { signal },
    );
  },
};
